import { EnvironmentProviders, Injectable, makeEnvironmentProviders } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { Auth, getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { catchError, from, map, Observable, of, ReplaySubject } from 'rxjs';
import { AppConfig } from '../../shared/app-config';
import { IUser } from '../../shell/authorization/models/user.interface';
import { UserRole } from '../../shell/authorization/models/user-role.enum';
import { AUTH_PROVIDER, AuthProviderService } from './auth-provider.interface';

const mapFirebaseUserToAppUser = (firebaseUser: { displayName?: string; email?: string }): IUser => {
  const displayName = firebaseUser.displayName?.trim() || firebaseUser.email || '';
  const [firstname, ...lastnameParts] = displayName.split(' ');

  return {
    firstname: firstname || 'User',
    lastname: lastnameParts.join(' '),
    email: firebaseUser.email || '',
    roles: [UserRole.Administrator, UserRole.Operator],
    token: '',
  };
};

/**
 * Firebase Authentication provider using @angular/fire.
 * Handles popup-based login/logout and token acquisition via Firebase SDK.
 *
 * Installation:
 * npm install firebase @angular/fire
 */
@Injectable()
export class FirebaseAuthService implements AuthProviderService {
  private _auth: Auth | null = null;
  private _currentUser: IUser | null = null;
  private readonly _currentUser$ = new ReplaySubject<IUser | null>(1);

  constructor() {
    this._initializeFirebase();
  }

  private _initializeFirebase(): void {
    if (!AppConfig.firebase) {
      console.warn('Firebase config not provided in AppConfig');
      this._setCurrentUser(null);
      return;
    }

    try {
      const firebaseApp = initializeApp(AppConfig.firebase);
      this._auth = getAuth(firebaseApp);

      // Listen for auth state changes
      if (this._auth) {
        onAuthStateChanged(this._auth, (firebaseUser) => {
          const appUser = firebaseUser ? mapFirebaseUserToAppUser(firebaseUser) : null;
          this._setCurrentUser(appUser);
        });
      }
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      this._setCurrentUser(null);
    }
  }

  private _setCurrentUser(user: IUser | null): void {
    this._currentUser = user;
    this._currentUser$.next(user);
  }

  currentUser(): IUser | null {
    return this._currentUser;
  }

  sessionChanges$(): Observable<IUser | null> {
    return this._currentUser$.asObservable();
  }

  accessToken$(resourceUrl: string): Observable<string | null> {
    void resourceUrl;
    if (!this._auth || !this._auth.currentUser) {
      return of(null);
    }

    return from(this._auth.currentUser.getIdToken()).pipe(catchError(() => of(null)));
  }

  login$(): Observable<string | undefined> {
    if (!this._auth) {
      return of('authentication.providerNotInitialized');
    }

    const provider = new GoogleAuthProvider();

    return from(signInWithPopup(this._auth, provider)).pipe(
      map(() => undefined),
      catchError((error) => {
        console.error('Firebase login error:', error);
        return of('authentication.loginFailed');
      })
    );
  }

  logout$(): Observable<void> {
    if (!this._auth) {
      return of(undefined);
    }

    return from(signOut(this._auth)).pipe(
      map(() => {
        this._setCurrentUser(null);
        return undefined;
      }),
      catchError((error) => {
        console.error('Firebase logout error:', error);
        return of(undefined);
      })
    );
  }

  clearSession(): void {
    this._setCurrentUser(null);
    if (this._auth) {
      signOut(this._auth).catch((error) => console.error('Error clearing session:', error));
    }
  }
}

/**
 * Provides Firebase Authentication to the application.
 * Include this provider in your app's providers array to enable Firebase login.
 *
 * Requires Firebase config in AppConfig.firebase.
 *
 * Installation:
 * npm install firebase @angular/fire
 *
 * @example
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     provideFirebaseAuth(),
 *     // ... other providers
 *   ]
 * });
 */
export function provideFirebaseAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    FirebaseAuthService,
    {
      provide: AUTH_PROVIDER,
      useExisting: FirebaseAuthService,
    },
  ]);
}
